/**
 * Main App Component — FBCA architecture with shared layouts.
 * @file src/web/App.tsx
 *
 * PageRouter auto-discovers every `features/*\/pages/**\/*.tsx` file
 * and mounts it at the matching URL. The `layouts` array here groups
 * those discovered pages by URL prefix so they share a React Router
 * layout route — which is what keeps chrome (Header, Sidebar, Footer)
 * MOUNTED across navigations inside the same group. Without this,
 * each page would remount its own chrome on every navigation and
 * flash.
 *
 * Layout group ordering matters: first match wins. Keep specific
 * prefixes (`/admin`, `/user/admin`) BEFORE broader ones.
 *
 * Adding a new group:
 *   1. Create a LayoutRoute component that renders <Outlet /> inside
 *      its chrome (see AdminLayoutRoute for the reference pattern).
 *   2. Add an entry to the `layouts` array below with a matcher.
 *   3. Remove any per-page chrome wrapping from pages the matcher
 *      covers — they now return content only.
 *
 * Pages that match NO layout render bare (kept inside a shared
 * Suspense boundary by the router). That's fine for single-page
 * things like `/unsubscribe` where a full chrome would be wrong.
 */

import { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { SandboxBanner } from './shared/components/SandboxBanner';
import { AuthProvider, AuthGuard, useAuth } from './features/auth';
import { UserProvider } from './features/user';
import { PageRouter, type RouteLayout } from './lib/page-router';
import { MarketingLayoutRoute } from './features/main/components/MarketingLayoutRoute';
import { SettingsLayoutRoute } from './features/settings/components/SettingsLayoutRoute';
import { PlatformLayoutRoute } from './features/platform/components/PlatformLayoutRoute';
import { CollegeLayoutRoute, isCollegePath } from './features/main/components/CollegeLayoutRoute';
import { useApplyBranding } from './shared/hooks/useBranding';
import { initTheme } from './shared/hooks/useDarkMode';

/** Route-transition fallback — shown while a lazy page chunk is fetched,
 *  so navigating between pages shows a clear spinner instead of an empty
 *  screen for that moment. */
function RouteLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center" role="status" aria-label="Loading">
      <Loader2 className="h-7 w-7 animate-spin" style={{ color: 'oklch(0.545 0.193 264)' }} />
    </div>
  );
}

const layouts: RouteLayout[] = [
  // Admin console. Catches the top-level admin pages plus the
  // user-CRUD pages inherited from userapp (which live under /user/admin).
  // Platform — super-admin only (admin.system). PlatformLayoutRoute
  // gates the role and bounces non-super-admin visitors to home.
  {
    match: (path) => path === '/platform' || path.startsWith('/platform/') || path === '/chat-log',
    Layout: PlatformLayoutRoute,
  },
  // Tenant settings — General, Plan, Users, Team, College, LLM usage.
  {
    match: (path) => path === '/settings' || path.startsWith('/settings/'),
    Layout: SettingsLayoutRoute,
  },
  // College workspace — rosters, readiness, shortlisting, imports. Every page
  // here calls a tenant() route, so an account with no institution (platform
  // admin, partner, support) gets a gate naming the login that opens it,
  // instead of the raw "not linked to an institution" error on a live page.
  {
    match: isCollegePath,
    Layout: CollegeLayoutRoute,
  },
  // Public marketing pages: homepage + about/contact/legal + anything
  // else under `features/main/pages/`. Kept last because the admin
  // matchers are more specific.
  //
  // TODO: If you add authenticated user pages (profile, account
  // settings) that want a different layout than marketing, create a
  // third LayoutRoute and insert it above this one.
  {
    match: (path) =>
      path === '/' ||
      path.startsWith('/blog') ||
      ['/about', '/contact', '/terms', '/privacy', '/refund', '/cancellation', '/security', '/waitlist', '/downloads'].includes(path) ||
      path === '/support' || path.startsWith('/support/'),
    Layout: MarketingLayoutRoute,
  },
];

/**
 * Route a signed-in user to the host that matches their scope, via the
 * token-login hand-off:
 *   (a) a PLATFORM ADMIN (global super-admin, no partner) who authenticated on
 *       a partner subdomain / custom domain → the ROOT platform site.
 *   (b) a PARTNER-scoped user (partner login, or a college that belongs to a
 *       partner) who authenticated on the apex / a wrong host → THEIR subdomain
 *       (<slug>.<baseDomain>), so the app stays partner-branded.
 * No-op once already on the correct host.
 */
function PartnerSubdomainRedirect() {
  const { user, token, isAuthenticated } = useAuth();
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const u = user as unknown as { partnerId?: string | null; partner?: { slug?: string }; partnerSlug?: string | null; role?: string; level?: string; studentId?: string } | null;
    const base = ((import.meta as unknown as { env: Record<string, string> }).env.VITE_BASE_DOMAIN || '').toLowerCase().trim();
    if (!base) return;
    const host = window.location.hostname.toLowerCase();
    const port = window.location.port ? `:${window.location.port}` : '';
    const onRoot = host === base || host === `www.${base}`;
    const handoff = (targetHost: string, student: boolean) =>
      window.location.replace(`${window.location.protocol}//${targetHost}${port}/auth/token-login?token=${encodeURIComponent(token)}${student ? '&type=student' : ''}`);

    // (a) A platform admin (global super-admin, no partner) authenticated on a
    // partner subdomain / custom domain → hand off to the ROOT platform site so
    // they land on the admin console, not a partner-branded tenant view.
    const isPlatformAdmin = u?.role === 'admin' && u?.level === 'system' && !u?.partnerId;
    if (isPlatformAdmin) {
      if (!onRoot) handoff(base, false);
      return;
    }

    // (b) A partner-scoped user (a partner login, or a college login whose
    // college belongs to a partner) authenticated on the apex / a wrong host →
    // hand off to THEIR subdomain so the app stays partner-branded.
    const slug = u?.partnerSlug ?? u?.partner?.slug;
    if (!slug) return;
    const expected = `${slug}.${base}`;
    if (host === expected) return; // already there
    const isStudent = u?.role === 'user' || !!u?.studentId;
    handoff(expected, isStudent);
  }, [isAuthenticated, token, user]);
  return null;
}

function App() {
  // Apply the white-label favicon (custom logo is read per-component via useBranding).
  useApplyBranding();
  // Apply the stored light/dark preference and start following the OS while
  // the preference is "system". The pre-paint script in index.html has
  // already stamped <html data-theme> by now; this re-applies it from the
  // same source of truth and wires up the matchMedia listener.
  useEffect(() => initTheme(), []);
  return (
    <Router basename="/">
      <SandboxBanner />
      <AuthProvider>
        <PartnerSubdomainRedirect />
        <UserProvider>
          {/* Global gate: AuthGuard inspects the current URL against
              config.routes.public / authOnly and redirects unauthenticated
              users away from protected pages. Public marketing + apply +
              accept routes pass through; everything else (students,
              companies, /team, /platform, …) bounces to /auth/login. */}
          <AuthGuard>
            <PageRouter layouts={layouts} fallback={<RouteLoading />} />
          </AuthGuard>
        </UserProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
